import { z } from 'zod'

/**
 * Coordinate schema
 */
export const CoordinateSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180)
})

/**
 * POI Category enum
 */
export const POICategorySchema = z.enum([
  'viewpoint',
  'restaurant',
  'monument',
  'park',
  'museum',
  'activity',
  'hotel',
  'cafe',
  'beach',
  'hiking',
  'other'
])

/**
 * POI schema
 */
export const POISchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(200),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  category: POICategorySchema,
  description: z.string().optional(),
  rating: z.number().min(1).max(5).optional(),
  uniqueness: z.number().min(0).max(100),
  distanceKm: z.number().optional(),
  relevance: z.number().min(0).max(100).optional(),
  confidence: z.number().min(0).max(100).optional(),
  verification: z
    .object({
      source: z.string().optional(),
      matched: z.boolean().optional(),
      confidence: z.number().min(0).max(100).optional(),
      distanceMeters: z.number().min(0).optional(),
      name: z.string().optional(),
      coordinates: CoordinateSchema.optional()
    })
    .optional(),
  photos: z.array(z.string().url()).optional(),
  openingHours: z.string().optional(),
  priceLevel: z.number().min(0).max(4).optional(),
  tags: z.array(z.string()).optional()
})

/**
 * Suggestion preferences
 */
export const SuggestionPreferencesSchema = z.object({
  prompt: z.string().max(500).optional(),
  avoidHighways: z.boolean().optional(),
  preferScenic: z.boolean().optional(),
  language: z.string().max(8).optional()
})

/**
 * Route context for suggestions
 */
export const RouteContextSchema = z.object({
  fromPoint: CoordinateSchema.extend({ 
    name: z.string().optional() 
  }).optional(),
  toPoint: CoordinateSchema.extend({ 
    name: z.string().optional() 
  }).optional(),
  detourMaxKm: z.number().min(0).max(100).optional(),
  timeAvailable: z.number().min(0).optional()
})

/**
 * Suggest POIs request
 */
export const SuggestPOIsRequestSchema = z.object({
  location: CoordinateSchema,
  categories: z.array(POICategorySchema).min(1).max(8),
  radiusKm: z.number().min(5).max(200).default(50),
  preferences: SuggestionPreferencesSchema.optional(),
  context: RouteContextSchema.optional(),
  planId: z.string().min(1).optional(),
  stageId: z.string().min(1).optional(),
  planName: z.string().min(1).max(120).optional(),
  stageName: z.string().min(1).max(120).optional()
})

/**
 * Stop for plan enhancement
 */
export const StopSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  lat: z.number(),
  lng: z.number(),
  stayMin: z.number().min(0).default(30),
  category: z.string().optional(),
  order: z.number().optional()
})

/**
 * Plan enhancement preferences
 */
export const EnhancementPreferencesSchema = z.object({
  style: z.enum(['relaxed', 'packed', 'balanced']).optional(),
  interests: z.array(z.string()).optional(),
  avoid: z.array(z.string()).optional(),
  prefer: z.array(z.string()).optional(),
  dailyDrivingMax: z.number().min(1).max(12).optional(),
  startTime: z.string().optional(),
  endTime: z.string().optional()
})

/**
 * Plan enhancement constraints
 */
export const EnhancementConstraintsSchema = z.object({
  mustVisit: z.array(z.string()).optional(),
  fixedStops: z.array(z.string()).optional(),
  maxStops: z.number().optional(),
  budget: z.number().optional()
})

/**
 * Enhance plan request
 */
export const EnhancePlanRequestSchema = z.object({
  planId: z.string(),
  stops: z.array(StopSchema),
  preferences: EnhancementPreferencesSchema.optional(),
  constraints: EnhancementConstraintsSchema.optional()
})

/**
 * Contextual suggestions request
 */
export const ContextualSuggestionsRequestSchema = z.object({
  currentLocation: CoordinateSchema,
  destination: CoordinateSchema,
  timeAvailable: z.number().min(0).optional(),
  query: z.string().max(200).optional()
})

/**
 * Dashboard contextual request schema
 */
export const DashboardSuggestionRequestSchema = z.object({
  planId: z.string(),
  context: z.enum(['pre', 'during', 'spark']),
  anchorStageId: z.string().optional(),
  categories: z.array(POICategorySchema).min(1).max(6).optional(),
  radiusKm: z.number().min(5).max(120).optional()
})

/**
 * Suggestion item in plan enhancement
 */
export const SuggestionItemSchema = z.object({
  type: z.enum(['add', 'replace', 'remove', 'modify', 'reorder']),
  targetId: z.string().optional(),
  position: z.number().optional(),
  spot: POISchema.optional(),
  reason: z.string(),
  impact: z.object({
    distanceChange: z.number().optional(),
    durationChange: z.number().optional(),
    uniquenessGain: z.number().optional()
  }).optional()
})

/**
 * POI suggestion response
 */
export const POISuggestionResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(POISchema),
  cached: z.boolean().optional(),
  fallback: z.boolean().optional(),
  metadata: z.object({
    model: z.string().optional(),
    processingTime: z.number().optional()
  }).optional()
})

/**
 * Plan enhancement response
 */
export const PlanEnhancementResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    suggestions: z.array(SuggestionItemSchema),
    optimizedRoute: z.array(z.string()).optional(),
    totalDistanceKm: z.number().optional(),
    estimatedDuration: z.number().optional(),
    metrics: z.object({
      originalDistance: z.number().optional(),
      optimizedDistance: z.number().optional(),
      timeSaved: z.number().optional(),
      uniquenessScore: z.number().optional()
    }).optional()
  })
})

/**
 * Queue status response
 */
export const QueueStatusResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    waiting: z.number(),
    active: z.number(),
    completed: z.number(),
    failed: z.number(),
    health: z.enum(['healthy', 'degraded', 'unhealthy'])
  })
})

export const DashboardSuggestionResponseSchema = z.object({
  context: z.enum(['pre', 'during', 'spark']),
  pois: z.array(POISchema),
  categories: z.array(POICategorySchema).optional(),
  radiusKm: z.number().optional(),
  metadata: z.object({
    source: z.string().optional(),
    processingTimeMs: z.number().optional(),
    cached: z.boolean().optional(),
    cachedAt: z.string().optional(),
    model: z.string().optional()
  }).optional()
})

/**
 * Export types
 */
export type Coordinate = z.infer<typeof CoordinateSchema>
export type POI = z.infer<typeof POISchema>
export type POICategory = z.infer<typeof POICategorySchema>
export type SuggestPOIsRequest = z.infer<typeof SuggestPOIsRequestSchema>
export type EnhancePlanRequest = z.infer<typeof EnhancePlanRequestSchema>
export type ContextualSuggestionsRequest = z.infer<typeof ContextualSuggestionsRequestSchema>
export type POISuggestionResponse = z.infer<typeof POISuggestionResponseSchema>
export type PlanEnhancementResponse = z.infer<typeof PlanEnhancementResponseSchema>
